// COPIED from ptc-core web/src/api/notifications.ts @ 998eb0a (unchanged since 98ca2bc) (fleet staff notifications, spec v0.2 step 2; CRM deploy
// #20), the fleet reference. Re-pin from core when it changes; change it in core first. Pinned by
// src/shell/notifications/notifications.conformance.test.tsx (N1–N8, copied with it). The deliberate differences:
//   - `useApi` is supervision's src/api/crmApi.ts: the CRM API with the staff member's own token, the same shape;
//   - a CRM item (a task move, a shipped report) links to the CRM's own page, as an ABSOLUTE url (`CRM_WEB_URL`):
//     those paths exist only there. An app event already carries its absolute link from core.
// SUP-03 — notification feed for the header bell.
//
// GET  /v1/notifications              — the caller's feed (unread first, newest first) + unreadCount
// POST /v1/notifications/{id}/read    — mark one read (idempotent)
//
// Freshness: the bell polls every 60s wherever the user is in the app; inside /supervision the
// shell's SignalR connection additionally invalidates this cache on targeted `notification.new`
// events (see useSignalR.ts), so updates there are instant.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import i18n from '../i18n';
import { useApi } from './crmApi';

export interface NotificationItem {
  id: string;
  workTaskId: string;
  taskType: string;
  periodKey: string | null;
  stageName: string;
  actorName: string | null;
  taskSubject: string | null;
  /** null = unread */
  readAt: string | null;
  createdAt: string;
  // An app event (fleet staff notifications, spec v0.2 step 2): taskType is APP_EVENT and these are set.
  /** The sending app's hub id, e.g. "supervision". */
  app?: string | null;
  /** The registered event type, e.g. "review.assigned". */
  type?: string | null;
  /** The event's parameters: data, interpolated untranslated. */
  params?: Record<string, string> | null;
  /** Absolute link into the sending app, carrying ?n=<id>. */
  link?: string | null;
  /** 1 = high: listed first, marked urgent. */
  priority?: number;
  /** On a digest: how many events this item stands for. */
  digestCount?: number | null;
  /** The English line the server rendered; shown only when this client has no keys for the type. */
  title?: string | null;
}

export interface NotificationsResponse {
  total: number;
  /** Unread across the whole feed (not just this page) — drives the badge. */
  unreadCount: number;
  page: number;
  pageSize: number;
  items: NotificationItem[];
}

export const notificationKeys = {
  all: ['notifications'] as const,
  page: ['notifications', 'page'] as const,
};

/** Must match NotificationsFunction.AppEventTaskType server-side. */
export const APP_EVENT = 'app-event';

export function isAppEvent(n: Pick<NotificationItem, 'taskType'>): boolean {
  return n.taskType === APP_EVENT;
}

/** The hub's list (/notifications): the latest 100, every app. */
export function useNotificationsPage() {
  const api = useApi();
  return useQuery({
    queryKey: notificationKeys.page,
    queryFn: () => api.get<NotificationsResponse>('/notifications?page=1&pageSize=100'),
    refetchInterval: POLL_INTERVAL_MS,
  });
}

/** Mark all read, optionally one app's only (POST /v1/notifications/read-all?app=). */
export function useMarkAllNotificationsRead() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (app?: string) =>
      api.post<{ marked: number }>(`/notifications/read-all${app ? `?app=${encodeURIComponent(app)}` : ''}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

const FEED_PAGE_SIZE = 15;
const POLL_INTERVAL_MS = 60_000;

export function useNotifications() {
  const api = useApi();
  return useQuery({
    queryKey: notificationKeys.all,
    queryFn: () =>
      api.get<NotificationsResponse>(`/notifications?page=1&pageSize=${FEED_PAGE_SIZE}`),
    refetchInterval: POLL_INTERVAL_MS,
  });
}

export function useMarkNotificationRead() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<NotificationItem>(`/notifications/${id}/read`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

/** The CRM's web app (ptc-core HubAppCatalog, `crm`): where a CRM item's own page lives. */
export const CRM_WEB_URL = 'https://green-water-0afd78b10.7.azurestaticapps.net';

/**
 * Where following a notification goes. An app event: its own absolute link (carrying ?n=). Anything else is a CRM item,
 * so its page is on the CRM, by task type: monthly-close → the monthly-close workbench; review-finding → the Findings
 * Inbox; a shipped report → the reporter's list; everything else → the generic task detail page.
 */
export function notificationTarget(n: Pick<NotificationItem, 'taskType' | 'workTaskId' | 'link'>): string {
  if (n.taskType === APP_EVENT) return n.link ?? '/notifications';
  return CRM_WEB_URL + crmPath(n);
}

function crmPath(n: Pick<NotificationItem, 'taskType' | 'workTaskId'>): string {
  switch (n.taskType) {
    case FEEDBACK_SHIPPED:
      return '/reported';
    case 'monthly-close':
      return `/bookkeeping/monthly-close/${n.workTaskId}`;
    case 'review-finding':
      return '/supervision/findings';
    default:
      return `/tasks/${n.workTaskId}`;
  }
}

/** A report of yours that shipped in a release (feedback contract v1.2): workTaskId is the report's id,
 *  taskSubject its FB-n, periodKey the release. Must match FeedbackCredit.NotificationType server-side. */
export const FEEDBACK_SHIPPED = 'feedback-shipped';

// SUP-06: the needs-review channel writes these StageName snapshots (no schema change — the
// discriminator rides the existing column). Must match TaskNeedsReviewFunction.RaisedStageName /
// ClearedStageName server-side.
const NEEDS_REVIEW_RAISED_STAGE = 'Needs review';
const NEEDS_REVIEW_CLEARED_STAGE = 'Needs review cleared';

/** One-line human message: "Helen R moved Monthly close — Acme (2026-05) to Review". */
export function notificationText(
  n: Pick<NotificationItem, 'actorName' | 'taskSubject' | 'taskType' | 'periodKey' | 'stageName'>,
  // Fleet Spanish item: whole-sentence templates, because word order differs by language. The
  // stage name, task subject and actor are DATA and are interpolated untranslated.
  t: TFunction = i18n.t
): string {
  if (n.taskType === APP_EVENT) return appEventText(n as NotificationItem, t);
  if (n.taskType === FEEDBACK_SHIPPED)
    return t('shell.notifications.feedbackShipped', { reference: n.taskSubject ?? '', release: n.periodKey ?? '' });
  const actor = n.actorName ?? t('shell.notifications.system');
  const what = n.taskSubject ?? n.taskType;
  const period = n.periodKey ? ` (${n.periodKey})` : '';
  // SUP-06: needs-review events read as flag/clear actions, not stage moves.
  if (n.stageName === NEEDS_REVIEW_RAISED_STAGE)
    return t('shell.notifications.flagged', { actor, what, period });
  if (n.stageName === NEEDS_REVIEW_CLEARED_STAGE)
    return t('shell.notifications.cleared', { actor, what, period });
  return t('shell.notifications.moved', { actor, what, period, stage: n.stageName });
}

/**
 * An app event's line, in the READER's language (spec §4): the type's whole-sentence template from the locale files
 * (`shell.notifications.app.<app>.<type with . as _>`, pinned equal to the server's registry by NotificationLocaleTests),
 * the params interpolated as data, a send-failure reason code rendered to its own words, and a digest's count line when
 * it stands for 2 or more. A type this build has no keys for falls back to the line the server rendered.
 */
export function appEventText(n: NotificationItem, t: TFunction = i18n.t): string {
  const key = `shell.notifications.app.${n.app ?? ''}.${(n.type ?? '').replace(/\./g, '_')}`;
  const digest = (n.digestCount ?? 1) >= 2;
  const params: Record<string, unknown> = { ...(n.params ?? {}) };
  if (typeof params.reason === 'string') params.reason = t(`shell.notifications.sendFailure.${params.reason}`, { defaultValue: params.reason });
  const k = digest ? `${key}_digest` : key;
  if (!i18n.exists(k)) return n.title ?? n.type ?? '';
  return t(k, { ...params, count: n.digestCount ?? 1 });
}
